import { useEffect, useMemo, useState } from 'react';

type LocalStatus = 'checking' | 'available' | 'unavailable';

const BUILD_ID = import.meta.env.VITE_BUILD_ID ?? 'local-uncommitted';
const DATASET_VERSION = 'synthetic-v0.1';

async function checkIndexedDb(): Promise<LocalStatus> {
  if (!('indexedDB' in globalThis)) return 'unavailable';
  return await new Promise((resolve) => {
    const request = indexedDB.open('nrs-spike-health', 1);
    request.onupgradeneeded = () => {
      request.result.createObjectStore('health', { keyPath: 'id' });
    };
    request.onerror = () => resolve('unavailable');
    request.onsuccess = () => {
      const db = request.result;
      const tx = db.transaction('health', 'readwrite');
      tx.objectStore('health').put({ id: 'last-check', checkedAt: new Date().toISOString() });
      tx.oncomplete = () => {
        db.close();
        resolve('available');
      };
      tx.onerror = () => {
        db.close();
        resolve('unavailable');
      };
    };
  });
}

export function App() {
  const [online, setOnline] = useState(navigator.onLine);
  const [storage, setStorage] = useState<LocalStatus>('checking');
  const [installed, setInstalled] = useState(window.matchMedia('(display-mode: standalone)').matches);
  const loadedAt = useMemo(() => new Date().toLocaleString(), []);

  useEffect(() => {
    void checkIndexedDb().then(setStorage);
    const handleOnline = () => setOnline(navigator.onLine);
    const displayMode = window.matchMedia('(display-mode: standalone)');
    const handleDisplay = () => setInstalled(displayMode.matches);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOnline);
    displayMode.addEventListener('change', handleDisplay);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOnline);
      displayMode.removeEventListener('change', handleDisplay);
    };
  }, []);

  return (
    <main>
      <header>
        <p className="eyebrow">NEW ROLE SEARCH · PHASE 1</p>
        <h1>Disposable Technical Validation Harness</h1>
        <p className="boundary">Non-production. Synthetic data only. No remote APIs, analytics, fonts or private evidence.</p>
      </header>

      <section aria-labelledby="status-heading">
        <h2 id="status-heading">Runtime status</h2>
        <dl className="status-grid">
          <div><dt>Network</dt><dd data-state={online ? 'ok' : 'warn'}>{online ? 'Online' : 'Offline'}</dd></div>
          <div><dt>Launch mode</dt><dd data-state={installed ? 'ok' : 'neutral'}>{installed ? 'Installed PWA' : 'Browser tab'}</dd></div>
          <div><dt>IndexedDB</dt><dd data-state={storage === 'available' ? 'ok' : storage === 'unavailable' ? 'warn' : 'neutral'}>{storage}</dd></div>
          <div><dt>Service worker</dt><dd data-state={'serviceWorker' in navigator ? 'ok' : 'warn'}>{'serviceWorker' in navigator ? 'Supported' : 'Unavailable'}</dd></div>
        </dl>
      </section>

      <section aria-labelledby="build-heading">
        <h2 id="build-heading">Build metadata</h2>
        <table>
          <tbody>
            <tr><th scope="row">Build ID</th><td>{BUILD_ID}</td></tr>
            <tr><th scope="row">Dataset</th><td>{DATASET_VERSION}</td></tr>
            <tr><th scope="row">Loaded</th><td>{loadedAt}</td></tr>
            <tr><th scope="row">User agent</th><td className="wrap-anywhere">{navigator.userAgent}</td></tr>
          </tbody>
        </table>
      </section>

      <section aria-labelledby="scope-heading">
        <h2 id="scope-heading">Spike scope</h2>
        <p>This shell supports SP-01 and SP-07 evidence collection only: installation, offline launch, safe shell update, strict static hosting and runtime network inspection.</p>
        <button type="button" onClick={() => window.location.reload()}>Reload shell</button>
      </section>
    </main>
  );
}
